import { Children, isValidElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GuideScene } from './GuideScene';

const SCENE_PREFIX = 'scene:';

function textOf(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') return String(child);
      if (isValidElement<{ children?: ReactNode }>(child)) return textOf(child.props.children);
      return '';
    })
    .join('');
}

/** Section anchor: "10. Cookies and Tracking Technologies" → "cookies-and-tracking-technologies". */
function headingSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/^\s*\d+\.\s*/, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// h2 sections get stable ids so links such as the cookie banner's
// /privacy-policy#cookies-and-tracking-technologies land on the right section. Site-relative
// links navigate client-side; everything else stays a plain anchor.
const COMPONENTS: Components = {
  h2: ({ children }) => <h2 id={headingSlug(textOf(children))}>{children}</h2>,
  a: ({ href, title, children }) =>
    href?.startsWith('/') && !href.startsWith('//') ? (
      <Link to={href} title={title}>
        {children}
      </Link>
    ) : (
      <a href={href} title={title}>
        {children}
      </a>
    ),
  // `![](scene:leasing)` on its own line embeds that guide scene (GuideScene). It becomes a
  // <div>, so the paragraph that would wrap it is dropped (a <div> inside a <p> is invalid HTML).
  p: ({ node, children }) => {
    const only = node?.children.length === 1 ? node.children[0] : undefined;
    const src = only?.type === 'element' && only.tagName === 'img' ? only.properties.src : undefined;
    return typeof src === 'string' && src.startsWith(SCENE_PREFIX) ? <>{children}</> : <p>{children}</p>;
  },
  img: ({ src, alt, title }) =>
    src?.startsWith(SCENE_PREFIX) ? (
      <div className="my-8">
        <GuideScene name={src.slice(SCENE_PREFIX.length)} />
      </div>
    ) : (
      <img src={src} alt={alt ?? ''} title={title} loading="lazy" />
    ),
};

interface MarkdownContentProps {
  children: string;
  variant?: 'policy' | 'article';
}

export function MarkdownContent({ children, variant = 'policy' }: MarkdownContentProps): JSX.Element {
  return (
    <div className={variant === 'article' ? 'marketing-prose marketing-prose--article' : 'marketing-prose'}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={COMPONENTS}
        urlTransform={(url) => (url.startsWith(SCENE_PREFIX) ? url : defaultUrlTransform(url))}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
